import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Minus,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Heading3,
  Type
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';

const MenuButton = ({ onClick, active, children, title, disabled }) => (
  <Button
    variant={active ? "default" : "ghost"}
    size="sm"
    onClick={onClick}
    title={title}
    type="button"
    className="h-8 w-8 p-0"
    disabled={disabled}
  >
    {children}
  </Button>
);

export const RichEditor = React.forwardRef(({ content, onChange, placeholder = "Comece a escrever...", onImageUpload }, ref) => {
  const [linkUrl, setLinkUrl] = React.useState('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4 cursor-pointer',
        },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'flex items-start gap-2',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Typography,
    ],
    content,
    editorProps: {
      attributes: {
        // Use prose-sm for smaller text (~14px) in technical analyses
        // Removed responsive scaling (sm:prose lg:prose-lg xl:prose-2xl) to keep consistent smaller size
        class: 'technical-analysis-content prose prose-sm max-w-none focus:outline-none min-h-[400px] p-4',
      },
      // We handle image clicks in AnalysisEditor.js with lightbox - no need to open new tab here
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when prop changes
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().setLink({ href: linkUrl }).run();
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleImageFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    // Check if onImageUpload function is provided
    if (!onImageUpload) {
      toast.error('Função de upload não configurada');
      return;
    }

    setUploadingImage(true);
    
    // Show loading toast
    const loadingToast = toast.loading(`Enviando ${file.name}...`);

    try {
      // Upload image and get URL
      const imageUrl = await onImageUpload(file);
      
      // Insert image at cursor position
      editor.chain().focus().setImage({ src: imageUrl, alt: file.name }).run();

      toast.dismiss(loadingToast);
      toast.success('Imagem inserida com sucesso!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.dismiss(loadingToast);
      toast.error('Não foi possível enviar a imagem. Tente novamente.');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Expose editor instance to parent via ref
  React.useImperativeHandle(ref, () => ({
    insertImage: (url, alt = 'Imagem') => {
      if (editor) {
        editor.chain().focus().setImage({ src: url, alt }).run();
        toast.success('Imagem inserida no conteúdo!');
      }
    },
    getEditor: () => editor
  }));

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-lg" >
      {/* Toolbar */}
      <div className="border-b p-2 flex flex-wrap gap-1" >
        {/* Headings */}
        <div className="flex gap-1 border-r pr-2" >
          <MenuButton
            onClick={() => editor.chain().focus().setParagraph().run()}
            active={editor.isActive('paragraph')}
            title="Parágrafo"
          >
            <Type className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive('heading', { level: 1 })}
            title="Título 1"
          >
            <Heading1 className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="Título 2"
          >
            <Heading2 className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="Título 3"
          >
            <Heading3 className="h-4 w-4" />
          </MenuButton>
        </div>

        {/* Text formatting */}
        <div className="flex gap-1 border-r pr-2" >
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Negrito (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Itálico (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Sublinhado (Ctrl+U)"
          >
            <UnderlineIcon className="h-4 w-4" />
          </MenuButton>
        </div>

        {/* Lists */}
        <div className="flex gap-1 border-r pr-2" >
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Lista com marcadores"
          >
            <List className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Lista numerada"
          >
            <ListOrdered className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            active={editor.isActive('taskList')}
            title="Checklist"
          >
            <CheckSquare className="h-4 w-4" />
          </MenuButton>
        </div>

        {/* Other blocks */}
        <div className="flex gap-1 border-r pr-2" >
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')}
            title="Citação"
          >
            <Quote className="h-4 w-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Linha divisória"
          >
            <Minus className="h-4 w-4" />
          </MenuButton>
        </div>

        {/* Link */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={editor.isActive('link') ? "default" : "ghost"}
              size="sm"
              type="button"
              className="h-8 w-8 p-0"
              title="Inserir link"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <label className="text-sm font-medium" >URL do link</label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://exemplo.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setLink();
                    }
                  }}
                />
                <Button onClick={setLink} size="sm">Inserir</Button>
              </div>
              {editor.isActive('link') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => editor.chain().focus().unsetLink().run()}
                >
                  Remover link
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Image Upload */}
        <MenuButton
          onClick={() => imageInputRef.current?.click()}
          title="Inserir imagem"
          disabled={uploadingImage}
        >
          <ImageIcon className="h-4 w-4" />
        </MenuButton>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          onChange={handleImageFileSelect}
          className="hidden"
        />
      </div>

      {/* Editor - with max height and internal scroll */}
      <div className="evolux-editor-content max-h-[600px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});
